import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageCurrency } from '../context/LanguageCurrencyContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';

const About = () => {
  const { t } = useTranslation();
  const { language } = useLanguageCurrency();
  const navigate = useNavigate();
  const testimonialsScrollRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [canScrollLeftTestimonials, setCanScrollLeftTestimonials] = useState(false);
  const [canScrollRightTestimonials, setCanScrollRightTestimonials] = useState(true);
  const [isTestimonialsHovered, setIsTestimonialsHovered] = useState(false);
  const testimonialsScrollIntervalRef = useRef(null);
  const visionSectionRef = useRef(null);


  // Handle window resize to update mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Set initial value
    setIsMobile(window.innerWidth < 768);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update scroll button visibility for testimonials
  const updateScrollButtonsTestimonials = () => {
    const container = testimonialsScrollRef.current;
    if (!container) return;
    
    const maxScroll = container.scrollWidth - container.clientWidth;
    setCanScrollLeftTestimonials(container.scrollLeft > 0);
    setCanScrollRightTestimonials(container.scrollLeft < maxScroll - 1);
  };

  // Scroll handlers
  const scrollLeftTestimonials = () => {
    const container = testimonialsScrollRef.current;
    if (!container) return;
    const cardWidth = container.querySelector('.flex-shrink-0')?.offsetWidth || 400;
    container.scrollBy({ left: -cardWidth - 24, behavior: 'smooth' });
  };

  const scrollRightTestimonials = () => {
    const container = testimonialsScrollRef.current;
    if (!container) return;
    const cardWidth = container.querySelector('.flex-shrink-0')?.offsetWidth || 400;
    container.scrollBy({ left: cardWidth + 24, behavior: 'smooth' });
  };

  // Continuous scroll on hover for testimonials
  const startScrollingLeftTestimonials = () => {
    if (testimonialsScrollIntervalRef.current) return;
    testimonialsScrollIntervalRef.current = setInterval(() => {
      const container = testimonialsScrollRef.current;
      if (!container) return;
      container.scrollBy({ left: -10, behavior: 'auto' });
    }, 16);
  };

  const startScrollingRightTestimonials = () => {
    if (testimonialsScrollIntervalRef.current) return;
    testimonialsScrollIntervalRef.current = setInterval(() => {
      const container = testimonialsScrollRef.current;
      if (!container) return;
      container.scrollBy({ left: 10, behavior: 'auto' });
    }, 16);
  };

  const stopScrollingTestimonials = () => {
    if (testimonialsScrollIntervalRef.current) {
      clearInterval(testimonialsScrollIntervalRef.current);
      testimonialsScrollIntervalRef.current = null;
    }
  };

  // Add scroll listeners
  useEffect(() => {
    const container = testimonialsScrollRef.current;
    
    if (container) {
      container.addEventListener('scroll', updateScrollButtonsTestimonials);
      // Initial check
      setTimeout(updateScrollButtonsTestimonials, 500);
    }
    
    return () => {
      if (container) container.removeEventListener('scroll', updateScrollButtonsTestimonials);
      if (testimonialsScrollIntervalRef.current) {
        clearInterval(testimonialsScrollIntervalRef.current);
      }
    };
  }, []);


  const features = [
    {
      icon: (
        <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: t('about.whyChooseUsItems.verifiedProperties'),
      description: t('about.whyChooseUsItems.verifiedPropertiesDesc'),
    },
    {
      icon: (
        <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: t('about.whyChooseUsItems.bestPrices'),
      description: t('about.whyChooseUsItems.bestPricesDesc'),
    },
    {
      icon: (
        <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: t('about.whyChooseUsItems.professionalService'),
      description: t('about.whyChooseUsItems.professionalServiceDesc'),
    },
    {
      icon: (
        <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: t('about.whyChooseUsItems.easyBooking'),
      description: t('about.whyChooseUsItems.easyBookingDesc'),
    },
  ];


  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B', width: '100%' }}>
      <Navbar />

      {/* Hero Section - Clean, Bold Headline */}
      <section 
        className="py-16 px-4 sm:px-6 lg:px-8 relative"
        style={{ 
          backgroundColor: '#1D2A3B',
          width: '100%',
          minHeight: 'auto',
          position: 'relative',
          paddingTop: 'calc(4rem + 80px)'
        }}
      >
        <div className="w-full max-w-6xl mx-auto relative z-10">
          {/* Large, Bold Hero Headline - Left Aligned */}
          <div className="mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight" style={{
              lineHeight: '1.3',
              letterSpacing: '-0.01em',
              maxWidth: '900px'
            }}>
              {language === 'ar' 
                ? 'نوفر طريقة جديدة وتعاونية للعمل'
                : 'Provide a New and Collaborative Way of Working'}
            </h1>
          </div>

          {/* Who Are We Section */}
          <div className="max-w-4xl">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-6" style={{
              lineHeight: '1.2'
            }}>
              {language === 'ar' ? 'من نحن' : 'Who Are We'}
            </h2>
            <p className="text-lg md:text-xl lg:text-2xl text-gray-200 leading-relaxed" style={{
              lineHeight: '1.7',
              maxWidth: '800px'
            }}>
              {language === 'ar' 
                ? 'نحن شركة نابضة بالحياة مكرسة لتقديم حلول عالمية تلبي احتياجات التعاون والعمل الجماعي للناس في جميع أنحاء العالم. منتجنا الأساسي هو HOSTA GLOBAL، منصة تجمع بين ميزات الحجز التعاونية وإدارة العقارات الفاخرة.'
                : 'We are a dynamic company delivering global solutions that empower collaboration and teamwork worldwide. Our core product, HOSTA GLOBAL, seamlessly combines collaborative booking with luxury property management to create exceptional digital experiences.'}
            </p>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 relative" style={{ 
        backgroundColor: '#1D2A3B',
        width: '100%', 
        minHeight: 'auto'
      }}>
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              {language === 'ar' ? 'ماذا نفعل' : 'What We Do'}
            </h2>
          </div>
          
          <div className="flex flex-col lg:flex-row gap-6 mb-12 w-full">
            {/* Product Screenshot/Graphic 1 */}
            <div className="relative rounded-2xl overflow-hidden shadow-xl flex-1" style={{
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(213, 181, 119, 0.2)'
            }}>
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80"
                alt={language === 'ar' ? 'منصة الحجز' : 'Booking Platform'}
                className="w-full h-full object-cover"
                style={{ minHeight: '300px', aspectRatio: '4/3' }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                <h3 className="text-xl font-bold text-white mb-2">
                  {language === 'ar' ? 'منصة حجز سهلة' : 'Easy Booking Platform'}
                </h3>
                <p className="text-gray-200 text-sm">
                  {language === 'ar' ? 'حجز سريع وآمن في خطوات بسيطة' : 'Quick and secure booking in simple steps'}
                </p>
              </div>
            </div>
            
            {/* Product Screenshot/Graphic 2 */}
            <div className="relative rounded-2xl overflow-hidden shadow-xl flex-1" style={{
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(213, 181, 119, 0.2)'
            }}>
              <img
                src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80"
                alt={language === 'ar' ? 'إدارة العقارات' : 'Property Management'}
                className="w-full h-full object-cover"
                style={{ minHeight: '300px', aspectRatio: '4/3' }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                <h3 className="text-xl font-bold text-white mb-2">
                  {language === 'ar' ? 'إدارة احترافية' : 'Professional Management'}
                </h3>
                <p className="text-gray-200 text-sm">
                  {language === 'ar' ? 'أدوات متقدمة لإدارة عقاراتك' : 'Advanced tools to manage your properties'}
                </p>
              </div>
            </div>
            
            {/* Product Screenshot/Graphic 3 */}
            <div className="relative rounded-2xl overflow-hidden shadow-xl flex-1" style={{
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(213, 181, 119, 0.2)'
            }}>
              <img
                src="https://images.unsplash.com/photo-1556912172-45b7abe8b7e1?w=800&q=80"
                alt={language === 'ar' ? 'تجربة ضيوف' : 'Guest Experience'}
                className="w-full h-full object-cover"
                style={{ minHeight: '300px', aspectRatio: '4/3' }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                <h3 className="text-xl font-bold text-white mb-2">
                  {language === 'ar' ? 'تجربة استثنائية' : 'Exceptional Experience'}
                </h3>
                <p className="text-gray-200 text-sm">
                  {language === 'ar' ? 'رعاية الضيوف من الحجز حتى المغادرة' : 'Guest care from booking to checkout'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Simple Paragraphs About What We Do */}
          <div className="max-w-4xl mx-auto space-y-4 text-center">
            <p className="text-lg md:text-xl text-gray-200 leading-relaxed" style={{ lineHeight: '1.8' }}>
              {language === 'ar' 
                ? 'نوفر منصة شاملة تربط الملاك والضيوف.'
                : 'We provide a comprehensive platform connecting owners and guests.'}
            </p>
            <p className="text-lg md:text-xl text-gray-200 leading-relaxed" style={{ lineHeight: '1.8' }}>
              {language === 'ar' 
                ? 'من البحث والحجز إلى الإدارة والدفع.'
                : 'From search and booking to management and payment.'}
            </p>
            <p className="text-lg md:text-xl text-gray-200 leading-relaxed" style={{ lineHeight: '1.8' }}>
              {language === 'ar' 
                ? 'كل شيء في مكان واحد. بسيط. آمن. موثوق.'
                : 'Everything in one place. Simple. Secure. Reliable.'}
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section - Symmetrical Layout */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 relative" style={{ 
        backgroundColor: 'rgba(29, 42, 59, 0.95)', 
        width: '100%', 
        minHeight: 'auto'
      }}>
        <div className="w-full max-w-7xl mx-auto relative">
          <div className="flex flex-col lg:flex-row items-stretch gap-8 lg:gap-8">
            {/* Left: Image Card - Symmetrical */}
            <div className="w-full lg:w-1/2 relative flex items-center">
              <div 
                className="relative overflow-hidden w-full"
                style={{ 
                  minHeight: '600px',
                  borderRadius: '24px',
                  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(213, 181, 119, 0.2), 0 0 40px rgba(213, 181, 119, 0.1)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 30px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(213, 181, 119, 0.3), 0 0 50px rgba(213, 181, 119, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(213, 181, 119, 0.2), 0 0 40px rgba(213, 181, 119, 0.1)';
                }}
              >
                <img
                  src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1600&q=80"
                  alt="Luxury modern property interior"
                  className="w-full h-full object-cover"
                  style={{ minHeight: '600px', width: '100%', height: '100%', display: 'block', borderRadius: '24px' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" style={{ borderRadius: '24px' }}></div>
              </div>
            </div>

            {/* Right: Content Panel - Symmetrical */}
            <div className="w-full lg:w-1/2 relative flex items-center">
              <div 
                className="relative p-8 md:p-10 lg:p-12 rounded-3xl w-full h-full flex flex-col justify-center"
                style={{
                  minHeight: '600px',
                  background: 'rgba(29, 42, 59, 0.85)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(213, 181, 119, 0.2)',
                  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(213, 181, 119, 0.1), 0 0 40px rgba(213, 181, 119, 0.1)',
                  borderRadius: '24px'
                }}
              >
                <div className="flex flex-col justify-center h-full">
                  <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 text-white" style={{
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)'
                  }}>
                    {t('about.ourMission')}
                  </h2>
                  
                  {/* Short Powerful Paragraph - Simple Lines */}
                  <div className="mb-8">
                    <p className="text-lg md:text-xl text-gray-200 mb-4 leading-relaxed" style={{
                      lineHeight: '1.8'
                    }}>
                      {language === 'ar' 
                        ? 'ربط ملاك العقارات بالضيوف'
                        : 'Connect property owners with guests'}
                    </p>
                    <p className="text-lg md:text-xl text-gray-200 mb-8 leading-relaxed" style={{
                      lineHeight: '1.8'
                    }}>
                      {language === 'ar' 
                        ? 'من خلال منصة سلسة وآمنة وسهلة الاستخدام.'
                        : 'Through a seamless, secure, and user-friendly platform.'}
                    </p>
                  </div>

                  {/* 3 Highlight Bullets with Icons - Centered */}
                  <div className="space-y-5 mb-8">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                          backgroundColor: 'rgba(213, 181, 119, 0.1)',
                          border: '1px solid rgba(213, 181, 119, 0.3)'
                        }}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-white">
                          {language === 'ar' ? 'تجربة حجز سلسة' : 'Seamless booking experience'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                          backgroundColor: 'rgba(213, 181, 119, 0.1)',
                          border: '1px solid rgba(213, 181, 119, 0.3)'
                        }}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-white">
                          {language === 'ar' ? 'عقارات فاخرة موثقة' : 'Verified luxury properties'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                          backgroundColor: 'rgba(213, 181, 119, 0.1)',
                          border: '1px solid rgba(213, 181, 119, 0.3)'
                        }}>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-white">
                          {language === 'ar' ? 'أسعار شفافة' : 'Transparent pricing'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Button */}
                  <button
                    onClick={() => navigate('/properties')}
                    className="px-8 py-3 font-semibold rounded-xl transition-all duration-200 transform hover:scale-105 w-fit"
                    style={{ 
                      backgroundColor: '#D5B577', 
                      color: '#1D2A3B',
                      boxShadow: '0 4px 12px rgba(213, 181, 119, 0.3)',
                      borderRadius: '12px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#c4a366';
                      e.target.style.boxShadow = '0 6px 16px rgba(213, 181, 119, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#D5B577';
                      e.target.style.boxShadow = '0 4px 12px rgba(213, 181, 119, 0.3)';
                    }}
                  >
                    {t('about.exploreProperties')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section 
        ref={visionSectionRef}
        className="py-16 px-4 sm:px-6 lg:px-8 relative"
        style={{ 
          backgroundColor: '#1D2A3B',
          width: '100%', 
          minHeight: '600px'
        }}
      >
        {/* Content Card */}
        <div className="relative z-10 max-w-4xl mx-auto">
          <div 
            className="relative p-10 md:p-12 lg:p-16 rounded-3xl text-center"
            style={{
              background: 'rgba(29, 42, 59, 0.85)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '2px solid rgba(213, 181, 119, 0.4)',
              boxShadow: '0 35px 70px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(213, 181, 119, 0.2), inset 0 0 40px rgba(213, 181, 119, 0.1)',
              borderRadius: '24px'
            }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8 text-white" style={{
              textShadow: '0 2px 20px rgba(0, 0, 0, 0.5)'
            }}>
              {t('about.ourVision')}
            </h2>
            
            {/* Vision Text with Glow on Keywords - Simple Lines */}
            <div className="space-y-4 mb-12">
              <p className="text-xl md:text-2xl text-gray-200 leading-relaxed" style={{
                lineHeight: '1.8',
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)'
              }}>
                {language === 'ar' ? (
                  <>أن نصبح منصة إيجار العقارات <span style={{ color: '#D5B577', textShadow: '0 0 20px rgba(213, 181, 119, 0.5)' }}>الأكثر ثقة</span> في العالم.</>
                ) : (
                  <>To become the world's most <span style={{ color: '#D5B577', textShadow: '0 0 20px rgba(213, 181, 119, 0.5)' }}>trusted</span> property rental platform.</>
                )}
              </p>
              <p className="text-xl md:text-2xl text-gray-200 leading-relaxed" style={{
                lineHeight: '1.8',
                textShadow: '0 2px 10px rgba(0, 0, 0, 0.5)'
              }}>
                {language === 'ar' ? (
                  <>و <span style={{ color: '#D5B577', textShadow: '0 0 20px rgba(213, 181, 119, 0.5)' }}>الأكثر ابتكاراً</span> في تقديم تجارب استثنائية.</>
                ) : (
                  <>And the most <span style={{ color: '#D5B577', textShadow: '0 0 20px rgba(213, 181, 119, 0.5)' }}>innovative</span> in delivering exceptional experiences.</>
                )}
              </p>
            </div>

            {/* Minimal Icon Row */}
            <div className="flex items-center justify-center gap-8 md:gap-12 flex-wrap">
              <div className="flex flex-col items-center gap-2">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-sm font-semibold text-white">{language === 'ar' ? 'ثقة' : 'Trust'}</span>
              </div>
              <div className="w-px h-12" style={{ backgroundColor: 'rgba(213, 181, 119, 0.3)' }}></div>
              <div className="flex flex-col items-center gap-2">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span className="text-sm font-semibold text-white">{language === 'ar' ? 'ابتكار' : 'Innovation'}</span>
              </div>
              <div className="w-px h-12" style={{ backgroundColor: 'rgba(213, 181, 119, 0.3)' }}></div>
              <div className="flex flex-col items-center gap-2">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                <span className="text-sm font-semibold text-white">{language === 'ar' ? 'تميز' : 'Excellence'}</span>
              </div>
              <div className="w-px h-12" style={{ backgroundColor: 'rgba(213, 181, 119, 0.3)' }}></div>
              <div className="flex flex-col items-center gap-2">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <span className="text-sm font-semibold text-white">{language === 'ar' ? 'إتاحة' : 'Accessibility'}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ 
        backgroundColor: '#1D2A3B', 
        width: '100%', 
        minHeight: 'auto',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ 
          background: 'radial-gradient(circle, rgba(213, 181, 119, 0.5) 0%, transparent 70%)'
        }}></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-10" style={{ 
          background: 'radial-gradient(circle, rgba(213, 181, 119, 0.4) 0%, transparent 70%)'
        }}></div>

        <div className="w-full max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 px-2">
              {t('about.whyChooseUs')}
            </h2>
            <div className="max-w-3xl mx-auto px-4 space-y-3">
              <p className="text-base sm:text-lg md:text-xl text-gray-300">
                {language === 'ar' 
                  ? 'نقدم حلولاً شاملة لإدارة وحجز العقارات.'
                  : 'We provide comprehensive solutions for property management and booking.'}
              </p>
              <p className="text-base sm:text-lg md:text-xl text-gray-300">
                {language === 'ar' 
                  ? 'منصة واحدة. تجربة استثنائية. نتائج مضمونة.'
                  : 'One platform. Exceptional experience. Guaranteed results.'}
              </p>
            </div>
          </div>

          {/* Modern Layout: Video on top, features below */}
          <div className="flex flex-col gap-8 lg:gap-12 items-center">
            {/* Video Section - Full Width */}
            <div className="w-full max-w-4xl mx-auto">
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6 text-center">
                {t('about.howItWorks') || 'How It Works'}
              </h3>
              <div 
                className="rounded-3xl overflow-hidden shadow-2xl border relative group"
                style={{
                  width: '100%',
                  aspectRatio: '16/9',
                  backgroundColor: '#000',
                  borderColor: 'rgba(213, 181, 119, 0.2)',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(213, 181, 119, 0.1)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.4)';
                  e.currentTarget.style.boxShadow = '0 30px 60px -12px rgba(213, 181, 119, 0.2), 0 0 0 1px rgba(213, 181, 119, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.2)';
                  e.currentTarget.style.boxShadow = '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(213, 181, 119, 0.1)';
                }}
              >
                <video
                  src="http://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4"
                  controls
                  className="w-full h-full object-cover"
                  style={{ outline: 'none' }}
                  poster="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=80"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>

            {/* Features - Below Video */}
            <div className="w-full max-w-4xl mx-auto">
              <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-8">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex flex-col items-center gap-4 transition-all duration-300 hover:translate-y-2 text-center flex-1 min-w-[150px]"
                  >
                    <div className="flex-shrink-0">
                      <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-xl text-amber-400 transition-all duration-300"
                        style={{
                          background: 'linear-gradient(135deg, rgba(213, 181, 119, 0.15) 0%, rgba(213, 181, 119, 0.1) 100%)',
                          border: '1px solid rgba(213, 181, 119, 0.2)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(213, 181, 119, 0.25) 0%, rgba(213, 181, 119, 0.15) 100%)';
                          e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.4)';
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(213, 181, 119, 0.15) 0%, rgba(213, 181, 119, 0.1) 100%)';
                          e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.2)';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        <div className="w-7 h-7 sm:w-8 sm:h-8">
                          {feature.icon}
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base sm:text-lg md:text-xl font-bold text-white">
                        {feature.title}
                      </h3>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ 
        backgroundColor: 'rgba(29, 42, 59, 0.95)', 
        width: '100%', 
        minHeight: 'auto' 
      }}>
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {t('about.whatPeopleThink')}
            </h2>
            <p className="text-base max-w-2xl mx-auto" style={{ color: '#D5B577' }}>
              {t('common.discoverSatisfied')}
            </p>
          </div>

          <div 
            className="relative"
            onMouseEnter={() => setIsTestimonialsHovered(true)}
            onMouseLeave={() => setIsTestimonialsHovered(false)}
          >
            {/* Left Arrow - All Devices */}
            {canScrollLeftTestimonials && (
              <button
                onClick={scrollLeftTestimonials}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.25)';
                  e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.5)';
                  startScrollingLeftTestimonials();
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.15)';
                  e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                  stopScrollingTestimonials();
                }}
                className="absolute top-1/2 -translate-y-1/2 z-10 text-white rounded-full p-2 sm:p-3 shadow-2xl transition-all duration-300 hover:scale-110"
                aria-label="Scroll left"
                style={{ 
                  backgroundColor: 'rgba(213, 181, 119, 0.15)',
                  backdropFilter: 'blur(20px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                  border: '1px solid rgba(213, 181, 119, 0.3)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                  left: isTestimonialsHovered ? '0.5rem' : '-3rem',
                  opacity: isTestimonialsHovered ? 1 : 0,
                  pointerEvents: isTestimonialsHovered ? 'auto' : 'none'
                }}
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}

            {/* Right Arrow - All Devices */}
            {canScrollRightTestimonials && (
              <button
                onClick={scrollRightTestimonials}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.25)';
                  e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.5)';
                  startScrollingRightTestimonials();
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(213, 181, 119, 0.15)';
                  e.currentTarget.style.borderColor = 'rgba(213, 181, 119, 0.3)';
                  stopScrollingTestimonials();
                }}
                className="absolute top-1/2 -translate-y-1/2 z-10 text-white rounded-full p-2 sm:p-3 shadow-2xl transition-all duration-300 hover:scale-110"
                aria-label="Scroll right"
                style={{ 
                  backgroundColor: 'rgba(213, 181, 119, 0.15)',
                  backdropFilter: 'blur(20px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                  border: '1px solid rgba(213, 181, 119, 0.3)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                  right: isTestimonialsHovered ? '0.5rem' : '-3rem',
                  opacity: isTestimonialsHovered ? 1 : 0,
                  pointerEvents: isTestimonialsHovered ? 'auto' : 'none'
                }}
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            <div 
              ref={testimonialsScrollRef}
              className="flex gap-6 overflow-x-auto scrollbar-hide pb-4"
            style={{ 
              scrollBehavior: 'smooth',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
              paddingLeft: '1rem',
              paddingRight: '1rem',
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            {(() => {
              const testimonials = [
                {
                  id: 1,
                  name: t('about.testimonials.1.name'),
                  role: t('about.testimonials.1.role'),
                  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&q=80',
                  quote: t('about.testimonials.1.quote'),
                },
                {
                  id: 2,
                  name: t('about.testimonials.2.name'),
                  role: t('about.testimonials.2.role'),
                  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&q=80',
                  quote: t('about.testimonials.2.quote'),
                },
                {
                  id: 3,
                  name: t('about.testimonials.3.name'),
                  role: t('about.testimonials.3.role'),
                  image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&q=80',
                  quote: t('about.testimonials.3.quote'),
                },
                {
                  id: 4,
                  name: t('about.testimonials.4.name'),
                  role: t('about.testimonials.4.role'),
                  image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&q=80',
                  quote: t('about.testimonials.4.quote'),
                },
                {
                  id: 5,
                  name: t('about.testimonials.5.name'),
                  role: t('about.testimonials.5.role'),
                  image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&q=80',
                  quote: t('about.testimonials.5.quote'),
                },
                {
                  id: 6,
                  name: t('about.testimonials.6.name'),
                  role: t('about.testimonials.6.role'),
                  image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&q=80',
                  quote: t('about.testimonials.6.quote'),
                },
                {
                  id: 7,
                  name: t('about.testimonials.7.name'),
                  role: t('about.testimonials.7.role'),
                  image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&q=80',
                  quote: t('about.testimonials.7.quote'),
                },
                {
                  id: 8,
                  name: t('about.testimonials.8.name'),
                  role: t('about.testimonials.8.role'),
                  image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&q=80',
                  quote: t('about.testimonials.8.quote'),
                },
              ];
              // No duplication - use arrow navigation instead
              return testimonials;
            })().map((testimonial, index) => (
              <div
                key={`${testimonial.id}-${index}`}
                className="rounded-2xl p-8 shadow-lg hover:shadow-amber-500/20 transition-all duration-300 transform hover:-translate-y-2 border relative flex-shrink-0"
                style={{ minWidth: '380px', maxWidth: '380px', backgroundColor: '#1D2A3B', borderColor: 'rgba(213, 181, 119, 0.3)' }}
              >
                <div className="relative z-10">
                  <p className="text-white mb-6 leading-relaxed text-base">
                    {testimonial.quote}
                  </p>
                  <div className="flex items-center gap-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover border-4"
                      style={{ borderColor: 'rgba(213, 181, 119, 0.5)' }}
                    />
                    <div>
                      <h4 className="font-bold text-white">{testimonial.name}</h4>
                      <p className="text-sm" style={{ color: '#D5B577' }}>
                        {testimonial.role}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#1D2A3B', width: '100%', minHeight: 'auto' }}>
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4 px-2">
              {t('about.ourValues')}
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8 lg:gap-10">
            {/* Trust */}
            <div className="flex flex-col items-center justify-center p-6 bg-slate-800 rounded-2xl shadow-lg border border-amber-500/30 hover:border-amber-500/50 transition-all">
              <div className="w-16 h-16 mb-4 flex items-center justify-center">
                <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white text-center">{t('about.ourValuesItems.trust')}</h3>
            </div>
            
            {/* Accessibility */}
            <div className="flex flex-col items-center justify-center p-6 bg-slate-800 rounded-2xl shadow-lg border border-amber-500/30 hover:border-amber-500/50 transition-all">
              <div className="w-16 h-16 mb-4 flex items-center justify-center">
                <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white text-center">{language === 'ar' ? 'إتاحة' : 'Accessibility'}</h3>
            </div>
            
            {/* Innovation */}
            <div className="flex flex-col items-center justify-center p-6 bg-slate-800 rounded-2xl shadow-lg border border-amber-500/30 hover:border-amber-500/50 transition-all">
              <div className="w-16 h-16 mb-4 flex items-center justify-center">
                <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white text-center">{t('about.ourValuesItems.innovation')}</h3>
            </div>
            
            {/* Excellence */}
            <div className="flex flex-col items-center justify-center p-6 bg-slate-800 rounded-2xl shadow-lg border border-amber-500/30 hover:border-amber-500/50 transition-all">
              <div className="w-16 h-16 mb-4 flex items-center justify-center">
                <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#D5B577' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white text-center">{t('about.ourValuesItems.excellence')}</h3>
            </div>
          </div>
        </div>
      </section>

      {/* List Your Property Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(to right, #D5B577, #c4a366)', width: '100%', minHeight: 'auto' }}>
        <div className="w-full max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            {t('home.listPropertyTitle')}
          </h2>
          <p className="text-lg text-white mb-8" style={{ color: 'rgba(255, 255, 255, 0.95)' }}>
            {t('home.listPropertyText')}
          </p>
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/register-owner')}
              className="px-8 py-4 font-semibold rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg"
              style={{ backgroundColor: '#1D2A3B', color: '#D5B577' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2a3d56'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#1D2A3B'}
            >
              {t('home.becomePartner')}
            </button>
          </div>
        </div>
      </section>

      <Footer />

      <WhatsAppButton />
      
      {/* CSS Animations */}
      <style>{`
        @keyframes gradient-shift {
          0%, 100% { 
            background-position: 0% 50%;
            transform: translateY(0);
          }
          50% { 
            background-position: 100% 50%;
            transform: translateY(-3px);
          }
        }
        
        @keyframes gradient-shift-once {
          0% { 
            background-position: 0% 50%;
            transform: translateY(0);
            opacity: 0;
          }
          50% { 
            background-position: 100% 50%;
            transform: translateY(-3px);
            opacity: 1;
          }
          100% { 
            background-position: 0% 50%;
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes underline-expand {
          0% { 
            width: 0;
            opacity: 0;
          }
          50% {
            opacity: 0.8;
          }
          100% { 
            width: 100%;
            opacity: 0.8;
          }
        }
        
        .animate-underline-expand {
          animation: underline-expand 1.2s ease-out 0.5s forwards;
          width: 100%;
          opacity: 0.8;
        }
        
        @keyframes pulse-glow {
          0%, 100% { 
            opacity: 0.3;
            transform: scale(1);
          }
          50% { 
            opacity: 0.6;
            transform: scale(1.1);
          }
        }
        
        @keyframes line-expand {
          0% { 
            width: 0;
            opacity: 0;
          }
          100% { 
            width: 80px;
            opacity: 1;
          }
        }
        
        @keyframes line-expand-delayed {
          0% { 
            width: 0;
            opacity: 0;
          }
          50% {
            opacity: 0;
          }
          100% { 
            width: 80px;
            opacity: 1;
          }
        }
        
        @keyframes fade-in-up {
          0% { 
            opacity: 0;
            transform: translateY(20px);
          }
          100% { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes icon-float {
          0%, 100% { 
            transform: translateY(0) rotate(0deg);
          }
          50% { 
            transform: translateY(-8px) rotate(5deg);
          }
        }
        
        @keyframes sparkle {
          0%, 100% { 
            opacity: 0.3;
            transform: scale(1);
          }
          50% { 
            opacity: 1;
            transform: scale(1.5);
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-on-scroll {
          opacity: 0;
          animation: fade-in-up 0.8s ease-out forwards;
        }
        
        .about-us-text span {
          animation: letter-float-once 2s ease-out forwards;
        }
        
        @keyframes letter-float {
          0%, 100% { 
            transform: translateY(0) rotate(0deg);
          }
          50% { 
            transform: translateY(-4px) rotate(1deg);
          }
        }
        
        @keyframes letter-float-once {
          0% { 
            transform: translateY(20px) rotate(0deg);
            opacity: 0;
          }
          100% { 
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
        }
        
        .animate-line-expand {
          animation: line-expand 1.5s ease-out forwards;
        }
        
        .animate-underline-expand {
          animation: underline-expand 1.2s ease-out 0.5s forwards;
        }
        
        .animate-underline-expand {
          animation-fill-mode: forwards;
        }
        
        .animate-pulse-glow {
          animation: pulse-glow 3s ease-in-out infinite;
        }
        
        .animate-line-expand-delayed {
          animation: line-expand-delayed 1.5s ease-out 1s forwards;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out forwards;
          opacity: 0;
        }
        
        .animate-icon-float {
          animation: icon-float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default About;
